import { CartDrawerStateChangeEvent, CartDrawerCloseEvent, EnterAnimationStartEvent } from "@gob/events";
import { gsap } from "gsap";

const selectors = {
  menuToggle: '[data-menu-toggle]',
  mobileMenu: '[data-mobile-menu]',
  mobileMenuLink: '[data-mobile-menu] a',
  navGroup: '[data-nav-group]',
  navGroupClosed: '[data-nav-group="closed"]',
  navGroupOpen: '[data-nav-group="open"]',
  themeSection: 'section[data-theme]',
}

export class SiteHeader extends HTMLElement {
  #mobileMenuOpen = false;

  constructor() {
    super();

    this.toggleMenu = this.toggleMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this.openMenu = this.openMenu.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleCartDrawerStateChange = this.handleCartDrawerStateChange.bind(this);
    this.handleEnterAnimationStart = this.handleEnterAnimationStart.bind(this);
  }

  handleEnterAnimationStart() {
    const firstThemeSection = document.querySelector(selectors.themeSection);
    const theme = firstThemeSection.getAttribute('data-theme');
    if (theme) {
      this.setAttribute('data-theme', theme);
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeMenu();
    }
  }

  handleCartDrawerStateChange(event) {
    if (event.open && this.#mobileMenuOpen) {
      this.closeMenu();
    }
  }

  animateNavGroups(state) {
    const navTimeline = gsap.timeline();
    const showGroup = state === 'open' ? this.navGroupOpen : this.navGroupClosed;
    const hideGroup = state === 'open' ? this.navGroupClosed : this.navGroupOpen;

      navTimeline.fromTo(showGroup, {
        autoAlpha: 0,
        y: '-50%',
        pointerEvents: 'none',
      }, {
        autoAlpha: 1,
        duration: 0.95,
        y: 0,
        pointerEvents: 'auto',
        willChange: 'transform',
        ease: 'elastic.out(1, 0.975)',
      });

      navTimeline.fromTo(hideGroup, {
        opacity: 1,
        y: 0,
        pointerEvents: 'auto',
      }, {
        autoAlpha: 0,
        duration: 0.95,
        y: '50%',
        pointerEvents: 'none',
        willChange: 'transform',
        ease: 'elastic.out(1, 0.975)',
      }, 0);
  }

  openMenu() {
    this.#mobileMenuOpen = true;
    this.mobileMenu.removeAttribute('inert');
    this.mobileMenu.setAttribute('data-state', 'open');
    this.animateNavGroups('open');
    
    window.dispatchEvent(new CartDrawerCloseEvent());
  }

  closeMenu() {
    this.#mobileMenuOpen = false;
    this.mobileMenu.setAttribute('inert', '');
    this.mobileMenu.setAttribute('data-state', 'closed');
    this.animateNavGroups('closed');
  }

  toggleMenu() {
    this.#mobileMenuOpen = !this.#mobileMenuOpen;
    
    if (this.#mobileMenuOpen) {
      this.openMenu();
    } else {
      this.closeMenu();
    }
  }

  connectedCallback() {
    this.menuToggles = Array.from(this.querySelectorAll(selectors.menuToggle));
    this.mobileMenu = this.querySelector(selectors.mobileMenu);
    this.mobileMenuLinks = this.querySelectorAll(selectors.mobileMenuLink);
    this.navGroups = this.querySelectorAll(selectors.navGroup);
    this.navGroupClosed = this.querySelector(selectors.navGroupClosed);
    this.navGroupOpen = this.querySelector(selectors.navGroupOpen);

    this.menuToggles?.forEach(toggle => {
      toggle?.addEventListener('click', this.toggleMenu);
    });
    this.mobileMenuLinks?.forEach(link => {
      link?.addEventListener('click', this.closeMenu);
    });

    window.addEventListener('keydown', this.handleKeydown, { passive: true });
    window.addEventListener(EnterAnimationStartEvent.eventName, this.handleEnterAnimationStart, { passive: true });
    window.addEventListener(CartDrawerStateChangeEvent.eventName, this.handleCartDrawerStateChange, { passive: true });
  }

  disconnectedCallback() {
    this.menuToggles?.forEach(toggle => {
      toggle?.removeEventListener('click', this.toggleMenu);
    });
    this.mobileMenuLinks?.forEach(link => {
      link?.removeEventListener('click', this.closeMenu);
    });
    window.removeEventListener('keydown', this.handleKeydown, { passive: true });
    window.removeEventListener(EnterAnimationStartEvent.eventName, this.handleEnterAnimationStart, { passive: true });
    window.removeEventListener(CartDrawerStateChangeEvent.eventName, this.handleCartDrawerStateChange, { passive: true });
  }
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader);
}